/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import ReviewAnalytics from './reviewAnalytics';
import {
    AccessKey,
    ActivateHandle,
    ReviewChartData,
    ReviewSalesPeriod,
    ReviewStatsDisplay,
    ReviewTab
} from '../../util/interfaces';
import {onSetCrumbs} from '../../util/events';
import {ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {AppContext} from '../misc/appContext';
import {sendGaPageView} from '../../util/analytics';
import CustomPeriodController from '../salesDashboard/customPeriod/customPeriodController';
import useRight from '../misc/useRight';
import {getReviewChartData, getReviewTotals} from '../../util/requestSender';
import {ISO_DATE_FORMAT} from '../../util/constants';
import {getReviewAnalyticsMemValue, setReviewAnalyticsMemValue} from '../../util/reviewAnalyticsUtil';
import {useTranslation} from 'react-i18next';
import {useBreakpoint} from '../../util/useBreakpoint';
import {showsMobileReviewFeed} from '../../util/accessUtil';

export function getReviewDatesFromPeriod(period: ReviewSalesPeriod): [IDate | null, IDate | null] {
    if (period === ReviewSalesPeriod.TOTAL) return [null, null];
    const toDate = IDate.now();
    const fromDate: IDate = toDate.clone();
    if (period === ReviewSalesPeriod.SEVEN_DAYS) fromDate.subtract(7, 'days');
    else if (period === ReviewSalesPeriod.THIRTY_DAYS) fromDate.subtract(30, 'days');
    return [fromDate, toDate];
}

type LoadParams = {fromDate: IDate | null; toDate: IDate | null; period?: ReviewSalesPeriod};

export default function ReviewAnalyticsController() {
    const {t} = useTranslation();
    const [accessProducts, accessFeed, accessExport] = useRight(
        AccessKey.PAGE_REVIEWS_PRODUCTS,
        AccessKey.PAGE_REVIEWS_FEED,
        AccessKey.PAGE_REVIEWS_EXPORT
    );
    const context = useContext(AppContext);
    const bp = useBreakpoint();

    const allowedTabs = useMemo(() => {
        const allowed: Array<ReviewTab> = [];
        if (accessFeed) allowed.push(ReviewTab.FEED);
        if (accessProducts) allowed.push(ReviewTab.PRODUCTS);
        if (accessExport) allowed.push(ReviewTab.EXPORT);
        return allowed;
    }, [accessProducts, accessFeed, accessExport]);
    // On the bottom-nav breakpoint range the page reduces to the simplified
    // card feed (no chart/stats/tabs). Users without feed access keep the
    // regular layout, also on mobile — there is nothing else to show them.
    // Shared with app.tsx's fluid-width gate so the two can never disagree.
    const showMobileFeed = showsMobileReviewFeed(context.accountContext.account, bp.isMobileLayout);
    const defaultTab = useMemo(() => {
        return allowedTabs[0];
    }, [allowedTabs]);

    const defaultPeriod = getReviewAnalyticsMemValue('period') ?? ReviewSalesPeriod.THIRTY_DAYS;
    // getReviewDatesFromPeriod has no CUSTOM branch (it would return a 0-day
    // "today..today" window) — a remembered custom period must restore its
    // stored dates instead. This effect re-runs when the layout crosses back
    // into desktop (showMobileFeed dep below), so wrong defaults here would
    // clobber an active custom range on a mere resize/rotation.
    const [defaultFrom, defaultTo] =
        defaultPeriod === ReviewSalesPeriod.CUSTOM
            ? [
                  (getReviewAnalyticsMemValue('periodFrom') as IDate | null) ?? IDate.now(),
                  (getReviewAnalyticsMemValue('periodTo') as IDate | null) ?? IDate.now()
              ]
            : getReviewDatesFromPeriod(defaultPeriod);
    const [periodValue, setPeriodValue] = useState<ReviewSalesPeriod>(defaultPeriod);
    const [fromDate, setFromDate] = useState<IDate | null>(defaultFrom);
    const [toDate, setToDate] = useState<IDate | null>(defaultTo);
    const [tab, setTab] = useState<ReviewTab>(getTabFromParams());
    const [loadingChart, setLoadingChart] = useState(true);
    const [loadingCountStats, setLoadingCountStats] = useState(true);
    const [chartData, setChartData] = useState<Record<string, ReviewChartData>>({});
    const [totalPositive, setTotalPositive] = useState(0);
    const [totalNegative, setTotalNegative] = useState(0);
    const [totalPositiveIncrease, setTotalPositiveIncrease] = useState(0);
    const [totalNegativeIncrease, setTotalNegativeIncrease] = useState(0);
    const [npsScore, setNpsScore] = useState(0);
    const [periodNpsScore, setPeriodNpsScore] = useState(0);
    const [statsDisplay, setStatsDisplay] = useState<ReviewStatsDisplay>(ReviewStatsDisplay.STATS_ONLY);

    const customPeriodRef = useRef<ActivateHandle>(null);
    // Latest-request ids for the chart/totals loads — a slower, superseded
    // response (e.g. the breakpoint-crossing effect racing a period change)
    // must not overwrite the dates/stats of a newer request.
    const chartRequestSeq = useRef(0);
    const totalsRequestSeq = useRef(0);
    // Account the chart/totals are loaded for — the layout effect below skips
    // redundant reloads when crossing breakpoints with current data on screen.
    const loadedStatsForAccount = useRef<string | null>(null);

    function getTabFromParams(): ReviewTab {
        let _tab: ReviewTab = getReviewAnalyticsMemValue('tab') ?? defaultTab;
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('tab')) {
            const tab = searchParams.get('tab')!;
            if (tab === 'feed' && allowedTabs.includes(ReviewTab.FEED)) _tab = ReviewTab.FEED;
            else if (tab === 'settings' && allowedTabs.includes(ReviewTab.SETTINGS)) _tab = ReviewTab.SETTINGS;
        }
        setReviewAnalyticsMemValue('tab', _tab);
        return _tab;
    }

    function onClickStat(stat: 'all' | 'positive' | 'negative') {
        return function () {
            if (tab !== ReviewTab.FEED) onChangeTab(ReviewTab.FEED);
            // Wait for feed tab to display
            window.setTimeout(() => {
                document.dispatchEvent(new CustomEvent('reviewStatClick', {detail: stat}));
            }, 1);
        };
    }

    function onChangeStatsDisplay() {
        if (statsDisplay === ReviewStatsDisplay.FULL) setStatsDisplay(ReviewStatsDisplay.STATS_ONLY);
        else setStatsDisplay(ReviewStatsDisplay.FULL);
    }

    function onChangePeriod(selectedPeriod: ReviewSalesPeriod | '') {
        if (selectedPeriod === ReviewSalesPeriod.CUSTOM) return customPeriodRef.current?.activate();
        if (selectedPeriod === periodValue || selectedPeriod === '') return;
        const [fromDate, toDate] = getReviewDatesFromPeriod(selectedPeriod);
        setPeriodValue(selectedPeriod);
        setFromDate(fromDate);
        setToDate(toDate);
        setReviewAnalyticsMemValue('period', selectedPeriod);
        setReviewAnalyticsMemValue('periodFrom', fromDate);
        setReviewAnalyticsMemValue('periodTo', toDate);
        loadChartData({fromDate, toDate, period: selectedPeriod});
        loadReviewTotals({fromDate, toDate});
    }

    function onSubmitCustomPeriod(fromDate: IDate, toDate: IDate) {
        const period = ReviewSalesPeriod.CUSTOM;
        setPeriodValue(period);
        setFromDate(fromDate);
        setToDate(toDate);
        setReviewAnalyticsMemValue('period', period);
        setReviewAnalyticsMemValue('periodFrom', fromDate);
        setReviewAnalyticsMemValue('periodTo', toDate);
        loadChartData({fromDate, toDate, period});
        loadReviewTotals({fromDate, toDate});
    }

    function onChangeTab(_tab: ReviewTab) {
        setReviewAnalyticsMemValue('tab', _tab);
        setTab(_tab);
    }

    function loadReviewTotals(params: LoadParams) {
        const {account} = context.accountContext;
        if (!account) return;
        setLoadingCountStats(true);
        totalsRequestSeq.current += 1;
        const seq = totalsRequestSeq.current;
        const _fromDate = params.fromDate?.format(ISO_DATE_FORMAT) ?? null;
        const _toDate = params.toDate?.format(ISO_DATE_FORMAT) ?? null;
        getReviewTotals({
            data: {account_id: account.id, from_date: _fromDate, to_date: _toDate},
            callback: (data: ResponseData) => {
                if (seq !== totalsRequestSeq.current) return; // Superseded by a newer load
                setLoadingCountStats(false);
                if (data.success) {
                    const {
                        total_negative,
                        total_positive,
                        total_negative_increase,
                        total_positive_increase,
                        nps_score,
                        period_nps_score
                    } = data;
                    setTotalNegative(total_negative);
                    setTotalPositive(total_positive);
                    setTotalNegativeIncrease(total_negative_increase);
                    setTotalPositiveIncrease(total_positive_increase);
                    setNpsScore(nps_score);
                    setPeriodNpsScore(period_nps_score);
                }
            }
        });
    }

    function loadChartData(params: LoadParams) {
        const {account} = context.accountContext;
        if (!account) return;
        setLoadingChart(true);
        chartRequestSeq.current += 1;
        const seq = chartRequestSeq.current;
        getReviewChartData({
            data: {account_id: account.id, from_date: null, to_date: null},
            callback: (data: ResponseData) => {
                if (seq !== chartRequestSeq.current) return; // Superseded by a newer load
                setLoadingChart(false);
                if (data.success) {
                    if (params?.period) setPeriodValue(params.period);
                    setFromDate(params.fromDate);
                    setToDate(params.toDate);
                    setChartData(data.chart_data);
                }
            }
        });
    }

    useEffect(() => {
        // The simplified mobile feed shows neither the chart nor the stat
        // cards — skip their requests there. Crossing the breakpoint into the
        // regular layout (e.g. tablet rotation) re-runs this effect and loads,
        // but only ONCE per account: period changes go through their own
        // handlers, so data already on screen is current and a mere layout
        // round-trip must not refetch it.
        const accountId = context.accountContext.account?.id ?? null;
        if (!showMobileFeed && accountId && loadedStatsForAccount.current !== accountId) {
            loadedStatsForAccount.current = accountId;
            loadChartData({fromDate: defaultFrom, toDate: defaultTo});
            loadReviewTotals({fromDate: defaultFrom, toDate: defaultTo});
        }
        if (!allowedTabs.includes(tab)) setTab(allowedTabs[0]);
    }, [context.accountContext.account, showMobileFeed]);

    useEffect(() => {
        sendGaPageView('/review-analytics');
        onSetCrumbs([{label: t('reviewAnalytics.crumb', 'Review Inzicht'), route: '/review-analytics'}]);
    }, []);

    return (
        <>
            <ReviewAnalytics
                showMobileFeed={showMobileFeed}
                onChangePeriod={onChangePeriod}
                onChangeTab={onChangeTab}
                onClickStat={onClickStat}
                onChangeStatsDisplay={onChangeStatsDisplay}
                periodValue={periodValue}
                tab={tab}
                toDate={toDate}
                fromDate={fromDate}
                allowedTabs={allowedTabs}
                chartData={chartData}
                loadingChart={loadingChart}
                loadingStats={loadingCountStats}
                totalNegative={totalNegative}
                totalNegativeIncrease={totalNegativeIncrease}
                totalPositive={totalPositive}
                totalPositiveIncrease={totalPositiveIncrease}
                statsDisplay={statsDisplay}
                npsScore={npsScore}
                periodNpsScore={periodNpsScore}
            />
            <CustomPeriodController ref={customPeriodRef} onSubmit={onSubmitCustomPeriod} />
        </>
    );
}
